import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  UserRound,
  ShieldCheck,
  KeyRound,
  Settings2,
  MonitorSmartphone,
  SlidersHorizontal,
} from "lucide-react";
import QRCode from "qrcode";
import {
  api,
  post,
  put,
  requestAccountVerification,
  sessionExpired,
} from "./api";
import { AccountSessions } from "./AccountSessions";
import { accountChanged, avatarURL } from "./AccountRuntime";
import {
  optionalFeatures,
  type AccountProfile,
  type AccountPolicy,
} from "./accountTypes";
import type { CurrentUser } from "./types";
import {
  Button,
  Card,
  ErrorState,
  InlineNotice,
  Modal,
  PageHeader,
  PageLoading,
  StatusBadge,
} from "./components/ui";
import { useI18n } from "./i18n";

const tabs = [
  { id: "overview", zh: "账户概览", en: "Overview", icon: UserRound },
  { id: "profile", zh: "基本资料", en: "Profile", icon: UserRound },
  { id: "password", zh: "密码与凭据", en: "Password", icon: KeyRound },
  {
    id: "totp",
    zh: "两步认证",
    en: "Two-step verification",
    icon: ShieldCheck,
  },
];
const coreSection = (value: string) =>
  tabs.some((item) => item.id === value) ? value : "overview";
type AccountView = "account" | "devices" | "features";
const viewForSection = (value: string): AccountView =>
  ["devices", "records", "exit_history"].includes(value)
    ? "devices"
    : ["features", "lock", "preferences", "password_reset"].includes(value)
      ? "features"
      : "account";
const accountViews = [
  {
    id: "account" as const,
    zh: "账户资料与安全",
    en: "Account and security",
    icon: UserRound,
  },
  {
    id: "devices" as const,
    zh: "登录设备",
    en: "Login devices",
    icon: MonitorSmartphone,
  },
  {
    id: "features" as const,
    zh: "可选功能",
    en: "Optional features",
    icon: SlidersHorizontal,
  },
];
type Confirmation = {
  title: string;
  description: string;
  action: () => Promise<unknown>;
};
type Recovery = { codes: string[]; purpose: "totp" | "password" };
export function AccountCenter({
  restricted = false,
  initialTab,
}: {
  restricted?: boolean;
  initialTab?: string;
}) {
  const { language, setLanguage } = useI18n(),
    en = language === "en",
    text = (zh: string, english: string) => (en ? english : zh);
  const [view, setView] = useState<AccountView>(() =>
    viewForSection(
      initialTab ||
        new URLSearchParams(location.search).get("section") ||
        "overview",
    ),
  );
  const [tab, setTab] = useState(() =>
    coreSection(
      initialTab ||
        new URLSearchParams(location.search).get("section") ||
        "overview",
    ),
  );
  useEffect(() => {
    const syncTab = () => {
      const section =
        initialTab ||
        new URLSearchParams(location.search).get("section") ||
        "overview";
      setTab(coreSection(section));
      setView(viewForSection(section));
    };
    syncTab();
    addEventListener("popstate", syncTab);
    return () => removeEventListener("popstate", syncTab);
  }, [initialTab]);
  const [profile, setProfile] = useState<AccountProfile | null>(null),
    [user, setUser] = useState<CurrentUser | null>(null);
  const [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null),
    [lockSetup, setLockSetup] = useState(false),
    [minutes, setMinutes] = useState(5),
    [autoLock, setAutoLock] = useState(true);
  const [binding, setBinding] = useState<{
      secret: string;
      uri: string;
      qr: string;
    } | null>(null),
    [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<Recovery | null>(null),
    [saved, setSaved] = useState(false);
  const [records, setRecords] = useState<Record<string, unknown>[]>([]),
    [recordsLoading, setRecordsLoading] = useState(false),
    [filter, setFilter] = useState(""),
    [from, setFrom] = useState("");
  const load = useCallback(async () => {
    const [p, u] = await Promise.all([
      api<AccountProfile>("/me/account"),
      api<CurrentUser>("/me"),
    ]);
    setProfile(p);
    setUser(u);
    return p;
  }, []);
  useEffect(() => {
    void load().catch((e) => setError(String(e)));
    const refresh = () => void load().catch((e) => setError(String(e)));
    addEventListener("layla:account-updated", refresh);
    return () => removeEventListener("layla:account-updated", refresh);
  }, [load]);
  async function perform(
    action: () => Promise<unknown>,
    success = "操作已完成",
  ) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
      await load();
      accountChanged();
      setMessage(success);
      return true;
    } catch (e) {
      setError(String(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  function go(id: string) {
    setView("account");
    setTab(id);
    setError("");
    setMessage("");
    history.replaceState({}, "", `/account?section=${encodeURIComponent(id)}`);
  }
  function chooseView(next: AccountView) {
    setView(next);
    setError("");
    setMessage("");
    history.replaceState(
      {},
      "",
      `/account?section=${encodeURIComponent(next === "account" ? tab : next)}`,
    );
  }
  useEffect(() => {
    if (!profile || restricted || view !== "devices") return;
    const section =
      initialTab || new URLSearchParams(location.search).get("section");
    if (section === "records")
      document
        .getElementById("account-login-history")
        ?.scrollIntoView({ block: "start" });
  }, [Boolean(profile), view, initialTab, restricted]);
  useEffect(() => {
    if (
      !profile ||
      restricted ||
      view !== "devices" ||
      !profile.features.records
    )
      return;
    let active = true;
    setRecords([]);
    setRecordsLoading(true);
    const path = `/me/account/events?scope=login&result=${encodeURIComponent(filter)}${from ? `&from=${encodeURIComponent(new Date(`${from}T00:00:00`).toISOString())}` : ""}`;
    const refresh = () =>
      api<{ items: Record<string, unknown>[] }>(path)
        .then((data) => {
          if (active) setRecords(data.items);
        })
        .catch((e) => {
          if (active) setError(String(e));
        })
        .finally(() => {
          if (active) setRecordsLoading(false);
        });
    void refresh();
    const timer = setInterval(() => void refresh(), 30000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [view, restricted, filter, from, profile?.revision]);
  if (!profile || !user)
    return error ? (
      <ErrorState
        message={error}
        onRetry={() => void load().catch((e) => setError(String(e)))}
      />
    ) : (
      <PageLoading />
    );
  const local = user.auth_source === "local";
  async function toggle(id: string, enabled: boolean) {
    if (id === "lock" && enabled) {
      setMinutes(profile!.lock_minutes || 5);
      setAutoLock(profile!.lock_minutes > 0);
      setLockSetup(true);
      return;
    }
    if (
      id === "password_reset" &&
      enabled &&
      !profile!.password_recovery_ready
    ) {
      await generateCodes("password");
      return;
    }
    if (!enabled) {
      setConfirmation({
        title: "关闭此可选功能？",
        description:
          id === "records"
            ? "关闭后隐藏历史登录记录，必要登录事件继续记录。"
            : "关闭后停止提供功能，保留已有配置。",
        action: async () => {
          const grant =
            id === "lock"
              ? await requestAccountVerification("lock.disable")
              : undefined;
          await put("/me/account", {
            features: { [id]: false },
            grant_token: grant,
          });
        },
      });
      return;
    }
    await perform(
      () => put("/me/account", { features: { [id]: true } }),
      "功能已启用，可在对应页面使用。",
    );
  }
  async function generateCodes(purpose: "totp" | "password") {
    await perform(async () => {
      const grant = await requestAccountVerification(purpose + ".codes");
      const data = await post<Recovery>("/me/account/recovery-codes", {
        purpose,
        grant_token: grant,
      });
      setSaved(false);
      setRecovery(data);
    }, "恢复凭据已生成，请保存并确认。请勿共享给他人。");
  }
  async function bind() {
    await perform(async () => {
      const grant = await requestAccountVerification("totp.bind");
      const data = await post<{ secret: string; otpauth_uri: string }>(
        "/me/account/totp/begin",
        { grant_token: grant },
      );
      const qr = await QRCode.toDataURL(data.otpauth_uri, {
        width: 220,
        margin: 2,
        errorCorrectionLevel: "M",
      });
      setCode("");
      setBinding({ secret: data.secret, uri: data.otpauth_uri, qr });
    }, "使用验证器添加后，输入新的动态验证码确认。");
  }
  async function changeMFA(enabled: boolean) {
    setConfirmation({
      title: enabled ? "开启登录 TOTP" : "关闭登录 TOTP",
      description: enabled
        ? "之后密码验证通过仍需动态码。保留当前设备，退出其他设备。"
        : "保留验证器绑定；登录不再要求本平台动态码。其他设备退出。",
      action: async () => {
        const grant = await requestAccountVerification("totp.login");
        await put("/me/account/totp/login", { enabled, grant_token: grant });
      },
    });
  }
  const coreNavigation = (
    <nav className="account-tabs" aria-label="账户资料分页">
      {tabs
        .filter(
          (t) =>
            !restricted || ["overview", "profile", "password"].includes(t.id),
        )
        .map(({ id, zh, en: label, icon: Icon }) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            aria-current={tab === id ? "page" : undefined}
            onClick={() => go(id)}
          >
            <Icon />
            {text(zh, label)}
          </button>
        ))}
    </nav>
  );
  const notices = (
    <>
      {error && <InlineNotice tone="error">{error}</InlineNotice>}
      {message && <InlineNotice tone="success">{message}</InlineNotice>}
    </>
  );
  let content: ReactNode = null,
    lockContent: ReactNode = null,
    passwordRecoveryContent: ReactNode = null,
    preferencesContent: ReactNode = null;
  if (tab === "overview")
    content = (
      <AccountSection title={text("账户概览", "Account overview")}>
        <div className="account-identity">
          <AccountAvatar profile={profile} name={user.display_name} />
          <div>
            <h2>{user.display_name}</h2>
            <p>
              {user.username} ·{" "}
              {local ? text("本地账号", "Local account") : "OIDC"}
            </p>
          </div>
        </div>
        <div className="account-kv">
          <span>两步认证</span>
          <strong>
            {profile.totp_enabled
              ? "已开启登录验证"
              : profile.totp_bound
                ? "已绑定，登录验证未开启"
                : local
                  ? "未绑定验证器"
                  : "由身份提供方管理"}
          </strong>
        </div>
        <AccountDeviceSummary />
        <div className="account-kv">
          <span>本人可选功能</span>
          <strong>
            {optionalFeatures.filter((f) => profile.features[f.id]).length} /{" "}
            {optionalFeatures.length} 已启用
          </strong>
        </div>
        <div className="form-actions">
          <Button variant="primary" onClick={() => go("profile")}>
            修改资料
          </Button>
          <Button onClick={() => go("password")}>修改密码</Button>
          {!restricted && (
            <Button onClick={() => go("totp")}>管理验证器</Button>
          )}
        </div>
        {sessionStorage.getItem("layla-rebind-recommended") === "true" && (
          <InlineNotice tone="warning">
            验证器恢复已完成，旧验证器已失效。建议重新绑定并开启登录验证。
          </InlineNotice>
        )}
      </AccountSection>
    );
  if (tab === "profile")
    content = (
      <AccountSection title={text("基本资料与头像", "Profile and avatar")}>
        <div className="account-avatar-controls">
          <AccountAvatar profile={profile} name={user.display_name} />
          <label className="ui-button button-outline">
            更换头像
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="account-file"
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file)
                  void perform(
                    () =>
                      api("/me/account/avatar", {
                        method: "PUT",
                        body: file,
                        headers: {
                          "Content-Type":
                            file.type || "application/octet-stream",
                        },
                      }),
                    "头像已更新。",
                  );
              }}
            />
          </label>
          <Button
            disabled={busy || !profile.avatar}
            onClick={() =>
              void perform(
                () => api("/me/account/avatar", { method: "DELETE" }),
                "已恢复默认头像。",
              )
            }
          >
            移除
          </Button>
        </div>
        <p className="account-note">
          PNG/JPEG，最大 2 MiB、2048×2048；服务端统一处理为 256×256。
        </p>
        {local ? (
          <form
            className="account-form"
            key={user.username + user.display_name}
            onSubmit={(e) => {
              e.preventDefault();
              const data = new FormData(e.currentTarget);
              void perform(
                () =>
                  put("/me/profile", {
                    username: data.get("username"),
                    display_name: data.get("display_name"),
                  }),
                "资料已更新，下次请使用新的用户名登录。原有资源归属保持不变。",
              );
            }}
          >
            <Input label="登录用户名" name="username" value={user.username} />
            <Input
              label="显示名称 / 昵称"
              name="display_name"
              value={user.display_name}
            />
            <Button variant="primary" type="submit" disabled={busy}>
              保存资料
            </Button>
          </form>
        ) : (
          <InlineNotice>
            用户名、昵称和密码由身份提供方管理。本平台头像可独立维护。
          </InlineNotice>
        )}
      </AccountSection>
    );
  if (tab === "password")
    content = (
      <AccountSection
        title={text("修改本人密码", "Change password")}
        description="8–128 个 Unicode 字符；修改后保留当前设备并退出其他设备。"
      >
        {local ? (
          <form
            className="account-form"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget,
                data = new FormData(form);
              if (data.get("new_password") !== data.get("confirm")) {
                setError("两次输入的新密码不一致。");
                return;
              }
              void perform(async () => {
                await put("/me/password", {
                  current_password: data.get("current_password"),
                  new_password: data.get("new_password"),
                });
                form.reset();
              }, "新密码已生效，其他设备已退出。");
            }}
          >
            <Input label="当前密码" name="current_password" type="password" />
            <Input label="新密码" name="new_password" type="password" />
            <Input label="确认新密码" name="confirm" type="password" />
            <Button type="submit" variant="primary" disabled={busy}>
              更新密码
            </Button>
          </form>
        ) : (
          <InlineNotice>
            该密码由身份提供方管理，请到对应身份服务修改。
          </InlineNotice>
        )}
      </AccountSection>
    );
  if (tab === "totp")
    content = (
      <div className="account-stack">
        <AccountSection
          title="验证器"
          description="绑定与登录验证分别管理。每个动态码只能成功使用一次，请等待新的验证码再执行下一项安全操作。"
        >
          <div className="account-kv">
            <span>绑定状态</span>
            <strong>
              {local
                ? profile.totp_bound
                  ? "已绑定"
                  : "未绑定"
                : "由身份提供方管理"}
            </strong>
          </div>
          {local ? (
            <div className="form-actions">
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => void bind()}
              >
                {profile.totp_bound ? "重新绑定" : "绑定验证器"}
              </Button>
              {profile.totp_bound && (
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() =>
                    setConfirmation({
                      title: "解绑验证器",
                      description:
                        "旧验证器和 TOTP 恢复码失效，登录 TOTP 同步关闭，其他设备退出。",
                      action: async () => {
                        const grant =
                          await requestAccountVerification("totp.remove");
                        await api("/me/account/totp", {
                          method: "DELETE",
                          body: JSON.stringify({ grant_token: grant }),
                        });
                      },
                    })
                  }
                >
                  解绑
                </Button>
              )}
            </div>
          ) : (
            <InlineNotice>
              本地 TOTP 仅用于本地账号。外部登录的 MFA 规则由身份提供方负责。
            </InlineNotice>
          )}
        </AccountSection>
        {local && (
          <>
            <AccountSection
              title="TOTP 恢复码"
              description="验证码无法使用时的独立恢复途径，不等同于密码找回。"
            >
              <div className="account-kv">
                <span>恢复准备</span>
                <strong>
                  {profile.totp_recovery_ready ? "已确认保存" : "尚未确认保存"}
                </strong>
              </div>
              <Button
                disabled={busy || !profile.totp_bound}
                onClick={() => void generateCodes("totp")}
              >
                {profile.totp_recovery_ready
                  ? "重新生成恢复码"
                  : "生成并保存恢复码"}
              </Button>
            </AccountSection>
            <AccountSection
              title="登录时要求 TOTP"
              description="核心认证功能内部的安全策略，不是模块停用开关。"
            >
              <label className="account-switch">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="登录时要求 TOTP"
                  checked={profile.totp_enabled}
                  disabled={
                    busy ||
                    !profile.totp_bound ||
                    (!profile.totp_recovery_ready && !profile.totp_enabled)
                  }
                  onChange={(e) => void changeMFA(e.target.checked)}
                />
                <span>
                  {profile.totp_enabled
                    ? "已开启：密码之后必须验证动态码"
                    : "未开启：绑定不会自动要求登录验证"}
                </span>
              </label>
              <p className="account-note">
                开启前先绑定验证器并保存恢复码。启停均需重新验证本人。
              </p>
            </AccountSection>
          </>
        )}
      </div>
    );
  const featureOptions = (
    <div className="account-options">
      {optionalFeatures.map((f) => (
        <div className="account-option" key={f.id}>
          <div>
            <h3>{text(f.zh, f.en)}</h3>
            <p>{f.description}</p>
            {f.id === "password_reset" && !local && (
              <small>外部身份的密码恢复由身份提供方处理。</small>
            )}
          </div>
          <label className="account-switch">
            <input
              type="checkbox"
              role="switch"
              aria-label={`${profile.features[f.id] ? "关闭" : "启用"}${f.zh}`}
              checked={Boolean(profile.features[f.id])}
              disabled={busy || (f.id === "password_reset" && !local)}
              onChange={(e) => void toggle(f.id, e.target.checked)}
            />
            <span>{profile.features[f.id] ? "已启用" : "未启用"}</span>
          </label>
        </div>
      ))}
    </div>
  );
  if (profile.features.lock)
    lockContent = (
      <AccountSection
        title="账户锁屏"
        description="手动锁屏影响当前浏览器全部标签页；后台任务继续执行。空闲时间设置对本人账号生效。"
      >
        <div className="account-kv">
          <span>自动锁屏</span>
          <strong>
            {profile.lock_minutes > 0
              ? `${profile.lock_minutes} 分钟无操作`
              : "关闭，仅手动"}
          </strong>
        </div>
        <div className="account-kv">
          <span>解锁方式</span>
          <strong>{local ? "本人密码" : "身份提供方重新验证"}</strong>
        </div>
        <div className="form-actions">
          <Button
            variant="primary"
            disabled={busy}
            onClick={() =>
              void perform(
                () => post("/me/account/lock", {}),
                "当前浏览器已锁定。",
              )
            }
          >
            立即锁屏
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              setMinutes(profile.lock_minutes || 5);
              setAutoLock(profile.lock_minutes > 0);
              setLockSetup(true);
            }}
          >
            调整设置
          </Button>
        </div>
      </AccountSection>
    );
  const loginHistory = (
    <AccountSection
      id="account-login-history"
      title="历史登录记录"
      description="本人的登录成功与失败记录，默认最近 30 天，最多 100 条。"
    >
      {profile.features.records ? (
        <>
          <div className="account-filters">
            <label className="field">
              <span>结果筛选</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="">全部</option>
                <option value="success">成功</option>
                <option value="failure">失败</option>
              </select>
            </label>
            <label className="field">
              <span>起始日期</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
          </div>
          {recordsLoading ? <PageLoading /> : <RecordList records={records} />}
        </>
      ) : (
        <InlineNotice>
          历史登录记录尚未启用。请点击“可选功能”按钮启用后查看。
        </InlineNotice>
      )}
    </AccountSection>
  );
  if (profile.features.password_reset)
    passwordRecoveryContent = (
      <AccountSection
        title="密码找回"
        description="使用预先保存的一次性密码恢复凭据。若启用了登录 TOTP，还需要有效动态验证码。"
      >
        <InlineNotice>
          密码重置后全部会话退出；该功能不能仅凭用户名或原登录密码直接重置。
        </InlineNotice>
        <div className="form-actions">
          <Button
            disabled={busy}
            onClick={() => void generateCodes("password")}
          >
            重新生成密码恢复凭据
          </Button>
        </div>
      </AccountSection>
    );
  if (profile.features.preferences)
    preferencesContent = (
      <AccountSection
        title="个人偏好"
        description="偏好保存在当前浏览器，不跨设备同步；功能启用状态按账号管理。"
      >
        <form
          className="account-form"
          onSubmit={(e) => {
            e.preventDefault();
            const data = new FormData(e.currentTarget),
              theme = String(data.get("theme"));
            localStorage.setItem("layla-theme", theme);
            localStorage.setItem("layla-density", String(data.get("density")));
            document.documentElement.dataset.density = String(
              data.get("density"),
            );
            setLanguage(data.get("language") === "en" ? "en" : "zh");
            dispatchEvent(new CustomEvent("layla:theme", { detail: theme }));
            setMessage("偏好已保存到当前浏览器。");
          }}
        >
          <label className="field">
            <span>主题</span>
            <select
              name="theme"
              defaultValue={localStorage.getItem("layla-theme") || "system"}
            >
              <option value="system">跟随系统</option>
              <option value="light">浅色</option>
              <option value="dark">深色</option>
            </select>
          </label>
          <label className="field">
            <span>语言</span>
            <select name="language" defaultValue={language}>
              <option value="zh">简体中文</option>
              <option value="en">English</option>
            </select>
          </label>
          <label className="field">
            <span>显示密度</span>
            <select
              name="density"
              defaultValue={
                localStorage.getItem("layla-density") || "comfortable"
              }
            >
              <option value="comfortable">舒适</option>
              <option value="compact">紧凑</option>
            </select>
          </label>
          <Button variant="primary" type="submit">
            保存偏好
          </Button>
        </form>
      </AccountSection>
    );
  return (
    <div className="account-center">
      <PageHeader
        title={text("个人设置", "Personal settings")}
        description={text(
          "本人资料、账户安全与可选能力；系统配置从左侧设置进入。",
          "Your identity, security and optional features. System configuration is available in Settings.",
        )}
      />
      {!restricted && (
        <nav className="account-view-navigation" aria-label="个人设置导航">
          {accountViews.map(({ id, zh, en: label, icon: Icon }) => (
            <Button
              key={id}
              variant="outline"
              aria-pressed={view === id}
              onClick={() => chooseView(id)}
            >
              <Icon />
              {text(zh, label)}
            </Button>
          ))}
        </nav>
      )}
      {notices}
      {(restricted || view === "account") && (
        <Card
          title={text("账户资料与安全", "Account and security")}
          className="account-core-card"
        >
          {coreNavigation}
          {content}
        </Card>
      )}
      {!restricted && view === "devices" && (
        <div id="account-devices">
          <AccountSessions
            navigation={null}
            embedded
            notesEnabled={Boolean(profile.features.device_notes)}
          >
            {loginHistory}
          </AccountSessions>
        </div>
      )}
      {!restricted && view === "features" && (
        <div id="account-features">
          <Card
            title={text("可选功能", "Optional features")}
            description="先启用并完成设置，再使用。核心账户功能固定提供；项目策略从侧栏设置进入。"
            className="account-optional-card"
          >
            {featureOptions}
            {lockContent}
            {passwordRecoveryContent}
            {preferencesContent}
          </Card>
        </div>
      )}
      <Modal
        open={Boolean(confirmation)}
        title={confirmation?.title || "确认操作"}
        description={confirmation?.description || ""}
        onClose={() => {
          if (!busy) setConfirmation(null);
        }}
        footer={
          <>
            <Button disabled={busy} onClick={() => setConfirmation(null)}>
              取消
            </Button>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => {
                if (confirmation)
                  void perform(confirmation.action).then((ok) => {
                    if (ok) setConfirmation(null);
                  });
              }}
            >
              确认并继续
            </Button>
          </>
        }
      >
        {error && <InlineNotice tone="error">{error}</InlineNotice>}
      </Modal>
      <Modal
        open={lockSetup}
        title="账户锁屏设置"
        description="保存后才启用；关闭功能前需先解锁并重新验证本人。"
        onClose={() => {
          if (!busy) setLockSetup(false);
        }}
        footer={
          <>
            <Button disabled={busy} onClick={() => setLockSetup(false)}>
              取消
            </Button>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() =>
                void perform(
                  () =>
                    put("/me/account", {
                      features: { lock: true },
                      lock_minutes: autoLock ? minutes : 0,
                    }),
                  "锁屏设置已生效。",
                ).then((ok) => {
                  if (ok) setLockSetup(false);
                })
              }
            >
              保存并启用
            </Button>
          </>
        }
      >
        <div className="account-form">
          <label className="account-switch">
            <input
              type="checkbox"
              checked={autoLock}
              onChange={(e) => setAutoLock(e.target.checked)}
            />
            自动锁屏
          </label>
          {autoLock && (
            <label className="field">
              <span>空闲时长</span>
              <select
                value={minutes}
                onChange={(e) => setMinutes(Number(e.target.value))}
              >
                {[1, 5, 10, 15, 30, 60, 120].map((n) => (
                  <option key={n} value={n}>
                    {n} 分钟
                  </option>
                ))}
              </select>
            </label>
          )}
          <InlineNotice>
            在线心跳不计为用户操作，空闲时长单独判断。
          </InlineNotice>
          {error && <InlineNotice tone="error">{error}</InlineNotice>}
        </div>
      </Modal>
      <Modal
        open={Boolean(binding)}
        title={profile.totp_bound ? "重新绑定验证器" : "绑定验证器"}
        description="新验证器确认前旧验证器保持有效。二维码与手动密钥仅本次展示，不要共享。"
        onClose={() => {
          if (!busy)
            void api("/me/account/totp/pending", { method: "DELETE" }).finally(
              () => setBinding(null),
            );
        }}
      >
        {binding && (
          <form
            className="account-form"
            onSubmit={(e) => {
              e.preventDefault();
              void perform(
                () => post("/me/account/totp/confirm", { code }),
                "验证器已绑定，登录验证开关保持原状态。",
              ).then((ok) => {
                if (ok) {
                  setBinding(null);
                  sessionStorage.removeItem("layla-rebind-recommended");
                }
              });
            }}
          >
            <img
              className="account-qr"
              src={binding.qr}
              alt="验证器绑定二维码（由浏览器本地生成）"
            />
            <label className="field">
              <span>手动密钥</span>
              <code className="account-secret">{binding.secret}</code>
            </label>
            <label className="field">
              <span>新验证器动态码</span>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                required
              />
            </label>
            {error && <InlineNotice tone="error">{error}</InlineNotice>}
            <Button variant="primary" type="submit" disabled={busy}>
              确认绑定
            </Button>
          </form>
        )}
      </Modal>
      <Modal
        open={Boolean(recovery)}
        title={
          recovery?.purpose === "totp" ? "保存 TOTP 恢复码" : "保存密码恢复凭据"
        }
        description="只在生成时展示；新凭据生成后旧凭据失效。请离线安全保存。"
        onClose={() => {
          if (!busy) setRecovery(null);
        }}
      >
        {recovery && (
          <div className="account-form">
            <div className="account-codes">
              {recovery.codes.map((c) => (
                <code key={c}>{c}</code>
              ))}
            </div>
            <Button
              onClick={() => {
                const blob = new Blob(
                    [
                      `${recovery.purpose} recovery codes — Layla-TAB\n\n${recovery.codes.join("\n")}\n`,
                    ],
                    { type: "text/plain;charset=utf-8" },
                  ),
                  url = URL.createObjectURL(blob),
                  a = document.createElement("a");
                a.href = url;
                a.download = `layla-${recovery.purpose}-recovery-codes.txt`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
            >
              下载恢复凭据
            </Button>
            <label className="account-switch">
              <input
                type="checkbox"
                checked={saved}
                onChange={(e) => setSaved(e.target.checked)}
              />
              我已安全保存，了解这些凭据只能使用一次
            </label>
            {error && <InlineNotice tone="error">{error}</InlineNotice>}
            <Button
              variant="primary"
              disabled={busy || !saved}
              onClick={() =>
                void perform(async () => {
                  await post("/me/account/recovery-codes/confirm", {
                    purpose: recovery.purpose,
                  });
                  if (recovery.purpose === "password")
                    await put("/me/account", {
                      features: { password_reset: true },
                    });
                }, "恢复凭据已确认保存。").then((ok) => {
                  if (ok) setRecovery(null);
                })
              }
            >
              确认保存{recovery.purpose === "password" ? "并启用找回密码" : ""}
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
function AccountSection({
  title,
  description,
  children,
  id,
}: {
  title?: string;
  description?: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="account-section">
      {(title || description) && (
        <header>
          {title && <h3>{title}</h3>}
          {description && <p className="account-note">{description}</p>}
        </header>
      )}
      {children}
    </section>
  );
}
function Input({
  label,
  name,
  value,
  type = "text",
}: {
  label: string;
  name: string;
  value?: string;
  type?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        name={name}
        type={type}
        defaultValue={value}
        autoComplete={
          type === "password"
            ? name === "current_password"
              ? "current-password"
              : "new-password"
            : "off"
        }
        required={name !== "display_name"}
      />
    </label>
  );
}
function AccountAvatar({
  profile,
  name,
}: {
  profile: AccountProfile;
  name: string;
}) {
  return (
    <span className="account-avatar-large">
      {profile.avatar ? (
        <img src={avatarURL(profile)} alt="本人头像" />
      ) : (
        name
          .trim()
          .split(/\s+/)
          .map((word) => word[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
      )}
    </span>
  );
}
function AccountDeviceSummary() {
  const [data, setData] = useState({ total: 0, online: 0 });
  useEffect(() => {
    void api<typeof data>("/me/sessions")
      .then(setData)
      .catch(() => undefined);
  }, []);
  return (
    <div className="account-kv">
      <span>有效登录设备</span>
      <strong>
        {data.total} 个 · {data.online} 个在线
      </strong>
    </div>
  );
}
function RecordList({ records }: { records: Record<string, unknown>[] }) {
  const labels: Record<string, string> = {
    login_succeeded: "登录成功",
    login_failed: "登录失败",
    login_totp_failed: "登录动态码验证失败",
    profile_updated: "个人资料已更新",
    password_changed: "密码已修改",
    password_reset: "密码恢复完成",
    totp_bound: "验证器已绑定",
    totp_removed: "验证器已解绑",
    login_totp_changed: "登录 TOTP 设置已更新",
    totp_recovered: "验证器恢复完成",
    recovery_codes_regenerated: "恢复凭据已重新生成",
    account_features_changed: "可选功能设置已更新",
    project_policy_changed: "项目策略已更新",
    identity_verification_failed: "本人身份验证失败",
    workspace_unlocked: "工作区已解锁",
    device_revoked: "指定设备已退出",
    other_devices_revoked: "其他设备已退出",
    logout: "主动退出",
    remote_logout: "被远程退出",
    expired: "登录已到期",
    password_invalid: "密码不正确",
    totp_invalid: "动态码无效或已使用",
    invalid_credentials: "用户名或密码不正确",
    invalid_or_used_code: "验证码无效或已使用",
    security_changed: "安全设置变更",
    account_recovered: "账户恢复",
    replaced: "此浏览器重新登录",
    totp: "TOTP 恢复凭据",
    password: "密码恢复凭据",
  };
  const label = (value: unknown) =>
    labels[String(value)] || String(value || "");
  return (
    <div className="account-records">
      {!records.length ? (
        <p>暂无符合条件的记录。</p>
      ) : (
        records.map((r, index) => (
          <div className="account-record" key={String(r.id || index)}>
            <div>
              <strong>{String(label(r.event_type))}</strong>
              <small>
                {String(
                  `${r.device_name} · ${r.client_ip} · ${label(r.reason)}`,
                )}
              </small>
            </div>
            <div>
              <StatusBadge value={r.success ? "succeeded" : "failed"} />
              <small>{new Date(String(r.created_at)).toLocaleString()}</small>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export function AccountPolicySettings() {
  const [policy, setPolicy] = useState<AccountPolicy | null>(null),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    void api<AccountPolicy>("/settings/account")
      .then(setPolicy)
      .catch((e) => setError(String(e)));
  }, []);
  if (!policy) return error ? <ErrorState message={error} /> : <PageLoading />;
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await put("/settings/account", policy);
      setMessage("项目策略已保存。");
      accountChanged();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHeader
        title="账户功能与项目策略"
        description="项目级配置。本人资料、TOTP、锁屏和设备管理从左下角头像进入。"
      />
      <SystemAccountNav />
      <Card
        title="项目级可选能力"
        description="这些策略由管理员管理，个人不能关闭项目强制要求。"
      >
        <form className="account-form" onSubmit={save}>
          <label className="account-switch">
            <input
              type="checkbox"
              checked={policy.oidc_enabled}
              onChange={(e) =>
                setPolicy({ ...policy, oidc_enabled: e.target.checked })
              }
            />
            允许外部身份登录
          </label>
          <InlineNotice>
            启用后仍需在“身份认证”配置有效身份源。关闭不撤销已经存在的会话。
          </InlineNotice>
          <label className="field">
            <span>初始化账户引导</span>
            <select
              value={policy.initial_mode}
              onChange={(e) =>
                setPolicy({
                  ...policy,
                  initial_mode: e.target.value as AccountPolicy["initial_mode"],
                })
              }
            >
              <option value="off">不启用</option>
              <option value="remind">提醒修改初始用户名和密码</option>
              <option value="require">完成修改后才能访问工作区</option>
            </select>
          </label>
          <label className="account-switch">
            <input
              type="checkbox"
              checked={policy.sensitive_verification}
              onChange={(e) =>
                setPolicy({
                  ...policy,
                  sensitive_verification: e.target.checked,
                })
              }
            />
            敏感业务再次验证
          </label>
          <p className="account-note">
            当前接入操作：读取 S3
            存储连接配置。验证结果绑定当前会话、操作和有效期，单次消费。
          </p>
          {error && <InlineNotice tone="error">{error}</InlineNotice>}
          {message && <InlineNotice tone="success">{message}</InlineNotice>}
          <Button type="submit" variant="primary" disabled={busy}>
            保存项目策略
          </Button>
        </form>
      </Card>
    </>
  );
}
export function SystemAccountNav() {
  const [admin, setAdmin] = useState(false);
  useEffect(() => {
    void api<CurrentUser>("/me")
      .then((u) => setAdmin(u.role === "admin"))
      .catch(() => undefined);
  }, []);
  return (
    <nav className="settings-subnav" aria-label="系统设置分页">
      {[
        ["webdav", "WebDAV"],
        ["storage", "存储"],
        ["auth", "身份认证"],
        ["account-policy", "功能与策略"],
      ]
        .filter(([id]) => admin || id === "webdav")
        .map(([id, name]) => (
          <button
            key={id}
            className={location.pathname === `/${id}` ? "active" : ""}
            onClick={() => {
              history.pushState({}, "", `/${id}`);
              dispatchEvent(new PopStateEvent("popstate"));
            }}
          >
            <Settings2 />
            {name}
          </button>
        ))}
    </nav>
  );
}
